import { useSelector, useDispatch } from 'react-redux';
import { createStorefrontApiClient } from '@shopify/storefront-api-client';
import Cookies from 'universal-cookie';
import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  gql
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GET_CART } from './schemas/FetchCart';
import { CREATE_CART } from './schemas/CreateCart';
import { ADD_LINE_ITEM } from './schemas/AddLineItem';
import { UPDATE_LINE_ITEM } from './schemas/UpdateLineItem';
import { REMOVE_LINE_ITEM } from './schemas/RemoveLineItem';
import { isProductHidden } from '../../../config/hiddenProducts';

const REACT_APP_SHOPIFY_STOREFRONT_TOKEN = 'f249368e8e1c14b2ceecc8279fadb5c7';

const authLink = setContext((_, { headers }) => ({
  headers: {
    ...headers,
    'X-Shopify-Storefront-Access-Token': REACT_APP_SHOPIFY_STOREFRONT_TOKEN
  }
}));

const httpLink = createHttpLink({
  uri: 'https://checkout.merchforall.com/api/graphql'
});

const apolloClient = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache()
});

// Creates the client with Storefront API
const client = createStorefrontApiClient({
  storeDomain: 'https://checkout.merchforall.com/api/graphql',
  apiVersion: '2025-01',
  publicAccessToken: REACT_APP_SHOPIFY_STOREFRONT_TOKEN,
});

const cookies = new Cookies();

const PRODUCT_FOUND = 'shopify/PRODUCT_FOUND';
const COLLECTION_FOUND = 'shopify/COLLECTION_FOUND';
const CART_FOUND = 'shopify/CART_FOUND';
const SHOP_FOUND = 'shopify/SHOP_FOUND';
const ADD_VARIANT_TO_CART = 'shopify/ADD_VARIANT_TO_CART';
const UPDATE_QUANTITY_IN_CART = 'shopify/UPDATE_QUANTITY_IN_CART';
const REMOVE_LINE_ITEM_IN_CART = 'shopify/REMOVE_LINE_ITEM_IN_CART';
const OPEN_CART = 'shopify/OPEN_CART';
const CLOSE_CART = 'shopify/CLOSE_CART';
const CART_COUNT = 'shopify/CART_COUNT';

const initialState = {
  isCartOpen: false,
  cartCount: 0,
  cart: {}, // This will be transformed to look like the old checkout structure
  products: [],
  product: {},
  shop: {}
};

// Data transformation functions to maintain backward compatibility
const transformCartToCheckoutFormat = (cart) => {
  if (!cart) {
    // Return a properly structured empty cart object
    return {
      id: null,
      checkoutUrl: null,
      lineItems: [],
      lines: { edges: [] },
      subtotalPrice: null,
      totalPrice: null,
      totalTax: null
    };
  }
  
  return {
    id: cart.id,
    checkoutUrl: cart.checkoutUrl,
    webUrl: cart.checkoutUrl, // Backward compatibility alias for components
    // Transform lines.edges to lineItems for backward compatibility
    lineItems: cart.lines?.edges?.map(edge => ({
      id: edge.node.id,
      quantity: edge.node.quantity,
      title: edge.node.merchandise?.product?.title || '',
      variant: {
        id: edge.node.merchandise?.id || '',
        title: edge.node.merchandise?.title || '',
        price: {
          amount: edge.node.cost?.amountPerQuantity?.amount || '0',
          currencyCode: edge.node.cost?.amountPerQuantity?.currencyCode || 'USD'
        },
        image: edge.node.merchandise?.image ? {
          src: edge.node.merchandise.image.url,
          altText: edge.node.merchandise.image.altText,
          id: edge.node.merchandise.image.id,
          width: edge.node.merchandise.image.width,
          height: edge.node.merchandise.image.height
        } : null,
        selectedOptions: edge.node.merchandise?.selectedOptions || []
      },
      // Keep the original Cart API structure for new components
      ...edge.node
    })) || [],
    // Keep original cart structure for new components
    lines: cart.lines || { edges: [] },
    subtotalPrice: cart.cost?.subtotalAmount,
    totalPrice: cart.cost?.totalAmount,
    totalTax: cart.cost?.totalTaxAmount,
    // Add any other properties that components might expect
    ...cart
  };
};

const transformLineItemsToCartFormat = (lineItemsToAdd) => {
  return lineItemsToAdd.map(item => ({
    quantity: item.quantity,
    merchandiseId: item.variantId
  }));
};

export default (state = initialState, action) => {
  switch (action.type) {
    case COLLECTION_FOUND:
      return { ...state, products: action.payload };
    case PRODUCT_FOUND:
      return { ...state, product: action.payload };
    case CART_FOUND:
      return { ...state, cart: transformCartToCheckoutFormat(action.payload) };
    case SHOP_FOUND:
      return { ...state, shop: action.payload };
    case ADD_VARIANT_TO_CART:
      return { ...state, cart: transformCartToCheckoutFormat(action.payload) };
    case UPDATE_QUANTITY_IN_CART:
      return { ...state, cart: transformCartToCheckoutFormat(action.payload) };
    case REMOVE_LINE_ITEM_IN_CART:
      return { ...state, cart: transformCartToCheckoutFormat(action.payload) };
    case OPEN_CART:
      return { ...state, isCartOpen: true };
    case CLOSE_CART:
      return { ...state, isCartOpen: false };
    case CART_COUNT:
      return { ...state, cartCount: action.payload };
    default:
      return state;
  }
};

// Gets a collection based on that collection's id
// id should be base64encoded
function getCollection(id) {
  const encodedID = btoa(`gid://shopify/Collection/${id}`);
  return (dispatch) => {
    apolloClient
      .query({
        variables: { id: encodedID, productsFirst: 50 },
        query: gql`
          fragment VariantFragment on ProductVariant {
            id
            title
            price {... on MoneyV2 {amount, currencyCode}}
            compareAtPrice {
              ... on MoneyV2 {
                amount
                currencyCode
              }
            }
						quantityAvailable
            priceV2 {
              amount
              currencyCode
            }
            weight
            available: availableForSale
            sku
            image {
              id
              transformedSrc(maxWidth: 720)
            }
            selectedOptions {
              name
              value
            }
          }
          fragment CollectionFragment on Collection {
            id
            handle
            title
            updatedAt
          }
          fragment ProductFragment on Product {
            id
            availableForSale
            descriptionHtml
            description
            handle
            productType
            title
            tags
						totalInventory
            options {
              id
              name
              values
            }
            images(first: 250) {
              pageInfo {
                hasNextPage
                hasPreviousPage
              }
              edges {
                cursor
                node {
                  id
                  transformedSrc(maxWidth: 720)
                }
              }
            }
            variants(first: 250) {
              pageInfo {
                hasNextPage
                hasPreviousPage
              }
              edges {
                cursor
                node {
                  ...VariantFragment
                }
              }
            }
          }
          query ($id: ID!, $productsFirst: Int!) {
            node(id: $id) {
              __typename
              ...CollectionFragment
              ... on Collection {
                id
                products(first: $productsFirst) {
                  pageInfo {
                    hasNextPage
                    hasPreviousPage
                  }
                  edges {
                    cursor
                    node {
                      ...ProductFragment
                    }
                  }
                }
              }
            }
          }
        `
      })
      .then((resp) => {
        const now = new Date();

        dispatch({
          type: COLLECTION_FOUND,
          payload: resp?.data?.node?.products?.edges
            ? resp?.data?.node?.products?.edges
              .map((edge) => ({
                ...edge.node,
                variants: edge?.node?.variants?.edges.map(
                  (edge) => edge.node
                ),
                images: edge?.node?.images?.edges.map((edge) => edge.node),
                endDate:
                  edge?.node?.tags && edge.node.tags.length > 1
                    ? // tags are returned in inverted order, so date tag is 1st element if present
                    new Date(edge.node.tags[0])
                    : undefined
              }))
              .filter(
                (product) =>
                  ((product.endDate && new Date(product.endDate) > now) ||
                  !product.endDate) &&
                  !isProductHidden(product)
              )
            : []
        });
      });
  };
}

// Gets individual item based on id
function getProduct(id) {
  return async (dispatch) => {
    // TODO: Migrate to Storefront API for product fetching
    // For now, this function returns null as it's not critical for cart functionality
    // This can be implemented later using Storefront API if needed
    dispatch({
      type: PRODUCT_FOUND,
      payload: null
    });
    return null;
  };
}

// Creates initial cart state from Shopify
function createCart() {
  return async (dispatch) => {
    const cartCookie = cookies.get('cart');

    if (cartCookie) {
      try {
        const data = await client.request(GET_CART, {
          variables: { id: cartCookie }
        });
        
        if (data.data.cart) {
          dispatch({
            type: CART_FOUND,
            payload: data.data.cart
          });
        } else {
          // Cart doesn't exist, create a new one
          await createNewCart(dispatch);
        }
      } catch (error) {
        console.error('Error fetching cart:', error);
        // Cart doesn't exist or is invalid, create a new one
        await createNewCart(dispatch);
      }
    } else {
      // No cart cookie, create a new one
      await createNewCart(dispatch);
    }
  };
}

// Helper function to create a new cart
async function createNewCart(dispatch) {
  try {
    const data = await client.request(CREATE_CART);
    const { cartCreate: { cart } } = data.data;
    
    const aWeekFromNow = new Date();
    aWeekFromNow.setDate(aWeekFromNow.getDate() + 7);

    cookies.set('cart', cart.id, { path: '/', expires: aWeekFromNow });

    dispatch({
      type: CART_FOUND,
      payload: cart
    });
  } catch (error) {
    console.error('Error creating cart:', error);
  }
}

// Gets Shopify store information
function shopInfo() {
  return (dispatch) => {
    // TODO: Migrate to Storefront API for shop info
    // For now, this function returns a placeholder shop object
    // This can be implemented later using Storefront API if needed
    dispatch({
      type: SHOP_FOUND,
      payload: {
        name: 'Shop',
        description: 'Shop description',
        primaryDomain: {
          url: window.location.origin
        }
      }
    });
  };
}

// Adds variants to cart
function addVariantToCart(cartId, lineItemsToAdd) {
  return async (dispatch) => {
    try {
      const cartFormatItems = transformLineItemsToCartFormat(lineItemsToAdd);
      
      const result = await client.request(ADD_LINE_ITEM, {
        variables: {
          cartId: cartId,
          lines: cartFormatItems
        }
      });
      
      const { cartLinesAdd: { cart } } = result.data;
      dispatch({
        type: ADD_VARIANT_TO_CART,
        payload: cart
      });
      return cart;
    } catch (error) {
      console.error('Error adding variant to cart:', error);
      throw error;
    }
  };
}

// Helper to map options to variant
function variantForOptions(product, selectedOptions) {
  if (!product || !selectedOptions) return null;
  
  const variants = product.variants || [];
  
  // Find the variant that matches all selected options
  const matchingVariant = variants.find(variant => {
    if (!variant.selectedOptions || !Array.isArray(variant.selectedOptions)) {
      return false;
    }
    
    // Check if all selected options match the variant's options
    return variant.selectedOptions.every(variantOption => {
      const selectedValue = selectedOptions[variantOption.name];
      return selectedValue && selectedValue === variantOption.value;
    });
  });
  
  // Return the matching variant, or the first variant as fallback
  return matchingVariant || variants[0] || null;
}

// Updates quantity of line items in cart
function updateQuantityInCart(lineItemId, quantity, cartId) {
  const lineItemsToUpdate = [
    { id: lineItemId, quantity: parseInt(quantity, 10) }
  ];

  return async (dispatch) => {
    try {
      const result = await client.request(UPDATE_LINE_ITEM, {
        variables: {
          cartId: cartId,
          lines: lineItemsToUpdate
        }
      });
      
      const { cartLinesUpdate: { cart } } = result.data;
      dispatch({
        type: UPDATE_QUANTITY_IN_CART,
        payload: cart
      });
      return cart;
    } catch (error) {
      console.error('Error updating quantity in cart:', error);
      throw error;
    }
  };
}

// Removes line item from cart
function removeLineItemInCart(cartId, lineItemId) {
  return async (dispatch) => {
    try {
      const result = await client.request(REMOVE_LINE_ITEM, {
        variables: {
          cartId: cartId,
          lineIds: [lineItemId]
        }
      });
      
      const { cartLinesRemove: { cart } } = result.data;
      dispatch({
        type: REMOVE_LINE_ITEM_IN_CART,
        payload: cart
      });
    } catch (error) {
      console.error('Error removing line item from cart:', error);
      throw error;
    }
  };
}

// To close the cart
function handleCartClose() {
  return {
    type: CLOSE_CART
  };
}

// To open the cart
function handleCartOpen() {
  return {
    type: OPEN_CART
  };
}

// Set the count of items in the cart
function handleSetCount(count) {
  return {
    type: CART_COUNT,
    payload: count
  };
}

export function useShopify() {
  const dispatch = useDispatch();
  const cartStatus = useSelector(
    (appState) => appState.shopifyState.isCartOpen
  );
  const cartCount = useSelector((appState) => appState.shopifyState.cartCount);
  const products = useSelector((appState) => appState.shopifyState.products);
  const product = useSelector((appState) => appState.shopifyState.product);
  const featured = useSelector((appState) => appState.shopifyState.featured);
  const cartState = useSelector(
    (appState) => appState.shopifyState.cart
  );
  const shopDetails = useSelector((appState) => appState.shopifyState.shop);
  const fetchProduct = (id) => dispatch(getProduct(id));
  const fetchCollection = (id) => dispatch(getCollection(id));
  const createCheckout = () => dispatch(createCart());
  const createShop = () => dispatch(shopInfo());
  const closeCart = () => dispatch(handleCartClose());
  const openCart = () => dispatch(handleCartOpen());
  const setCount = (count) => dispatch(handleSetCount(count));

  const addVariant = (cartId, lineItemsToAdd) =>
    dispatch(addVariantToCart(cartId, lineItemsToAdd));
  const updateQuantity = (lineItemId, quantity, cartId) =>
    dispatch(updateQuantityInCart(lineItemId, quantity, cartId));
  const removeLineItem = (cartId, lineItemId) =>
    dispatch(removeLineItemInCart(cartId, lineItemId));

  const optionsToVariant = (product, selectedOptions) =>
    variantForOptions(product, selectedOptions);

  return {
    products,
    product,
    featured,
    cartStatus,
    cartState,
    checkoutState: cartState, // Backward compatibility alias
    cartCount,
    shopDetails,
    optionsToVariant,
    addVariant,
    fetchProduct,
    fetchCollection,
    createCheckout,
    createShop,
    closeCart,
    openCart,
    updateQuantity,
    removeLineItem,
    setCount
  };
}
